import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getCurrentProjectId, setCurrentProjectId } from '@/lib/api';

// PR-STEP2-3 — the operational project box the operator is currently inside.
// Picked on the three-tile home (HomePage). The id is mirrored into
// localStorage by lib/api so the axios interceptor can attach X-Project-Id to
// every request; the meta (code + name) is kept alongside for the UI chip.

export interface CurrentProject {
  id: string;
  code: string;   // PRJ-NEOM | PRJ-DUMPERS | PRJ-RIYADH
  nameEn: string;
}

interface CurrentProjectContextValue {
  projectId: string | null;
  project: CurrentProject | null;
  setProject: (p: CurrentProject | null) => void;
}

const CurrentProjectContext = createContext<CurrentProjectContextValue | null>(null);

const META_KEY = 'jasrhub_current_project_meta';

function readMeta(): CurrentProject | null {
  try {
    const raw = localStorage.getItem(META_KEY);
    if (!raw) return null;
    const m = JSON.parse(raw) as CurrentProject;
    if (m && typeof m.id === 'string') return m;
  } catch {
    /* corrupt value — treat as none */
  }
  return null;
}

export function CurrentProjectProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [project, setProjectState] = useState<CurrentProject | null>(() => {
    const id = getCurrentProjectId();
    const meta = readMeta();
    return id && meta && meta.id === id ? meta : null;
  });

  const setProject = useCallback(
    (p: CurrentProject | null) => {
      setCurrentProjectId(p?.id ?? null);
      if (p) localStorage.setItem(META_KEY, JSON.stringify(p));
      else localStorage.removeItem(META_KEY);
      setProjectState(p);
      // The active box changed: refetch every scoped list so no other
      // project's rows linger on screen.
      void queryClient.invalidateQueries();
    },
    [queryClient],
  );

  return (
    <CurrentProjectContext.Provider value={{ projectId: project?.id ?? null, project, setProject }}>
      {children}
    </CurrentProjectContext.Provider>
  );
}

export function useCurrentProject(): CurrentProjectContextValue {
  const ctx = useContext(CurrentProjectContext);
  if (!ctx) throw new Error('useCurrentProject must be used within CurrentProjectProvider');
  return ctx;
}
